import { useState, type FormEvent } from "react";
import { Button } from "@/components/ui/button";
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { useAuthContext } from "@/hooks/useAuthContext";
import { useAuthStore } from "@/stores/auth-store";
import { STRINGS } from "@/constants/strings";
import { LoaderCircleIcon } from "lucide-react";

export default function SetPasswordPage() {
  const { completeNewPassword } = useAuthContext();
  const error = useAuthStore((s) => s.error);
  const [localError, setLocalError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setLocalError(null);
    const form = event.currentTarget;
    const password = (
      form.elements.namedItem("newPassword") as HTMLInputElement
    ).value;
    const confirm = (
      form.elements.namedItem("confirmPassword") as HTMLInputElement
    ).value;

    if (password !== confirm) {
      setLocalError(STRINGS.auth.passwordMismatch);
      return;
    }

    setIsSubmitting(true);
    try {
      await completeNewPassword(password);
    } catch {
      // error set in store
    } finally {
      setIsSubmitting(false);
    }
  }

  const displayError = localError ?? error;

  return (
    <div className="flex min-h-svh flex-col items-center justify-center gap-6 bg-background p-6 md:p-10">
      <div className="w-full max-w-sm">
        <form onSubmit={handleSubmit}>
          <FieldGroup>
            <div className="flex flex-col items-center gap-2 text-center">
              <h1 className="text-xl font-bold">
                {STRINGS.auth.setPasswordTitle}
              </h1>
              <FieldDescription>
                {STRINGS.auth.setPasswordDescription}
              </FieldDescription>
            </div>

            {displayError && (
              <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                {displayError}
              </div>
            )}

            <Field>
              <FieldLabel htmlFor="newPassword">
                {STRINGS.auth.newPasswordLabel}
              </FieldLabel>
              <Input
                id="newPassword"
                name="newPassword"
                type="password"
                placeholder={STRINGS.auth.newPasswordPlaceholder}
                autoComplete="new-password"
                required
                disabled={isSubmitting}
              />
            </Field>

            <Field>
              <FieldLabel htmlFor="confirmPassword">
                {STRINGS.auth.confirmPasswordLabel}
              </FieldLabel>
              <Input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                placeholder={STRINGS.auth.confirmPasswordPlaceholder}
                autoComplete="new-password"
                required
                disabled={isSubmitting}
              />
            </Field>

            <Field>
              <Button
                type="submit"
                className="w-full"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <LoaderCircleIcon className="animate-spin" />
                    {STRINGS.auth.settingPassword}
                  </>
                ) : (
                  STRINGS.auth.setPasswordButton
                )}
              </Button>
            </Field>
          </FieldGroup>
        </form>
      </div>
    </div>
  );
}
